import React, { useEffect, useState } from "react";
import axios from "axios";
import { PCB_CORE_URL } from "../../config";
import "./profile.css";
import { clearAuthSession, getAccessToken } from "../../authSession";

export default function Profile() {
  const API_BASE_URL = (PCB_CORE_URL || "").replace(/\/+$/, "");

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [user, setUser] = useState(null);
  const [form, setForm] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const http = axios.create({
    baseURL: API_BASE_URL,
    timeout: 20000,
  });

  http.interceptors.request.use((req) => {
    const token = getAccessToken();
    if (token) req.headers.Authorization = `Bearer ${token}`;
    return req;
  });

  const normalizeError = (e, fallback) => {
    const d = e?.response?.data;
    if (!d) return fallback;
    if (typeof d === "string") {
      return /<\/?[a-z][\s\S]*>/i.test(d) ? fallback : d;
    }
    if (d.detail) return Array.isArray(d.detail) ? d.detail.join(" ") : d.detail;
    return fallback;
  };

  function toForm(data) {
    return {
      first_name: data?.first_name || "",
      last_name: data?.last_name || "",
      company_name: data?.company_name || "",
      company_identifier: data?.company_identifier || "",
      phone_number: data?.phone_number || "",
      country: data?.country || "",
      billing_address_line1: data?.billing_address_line1 || "",
      billing_address_line2: data?.billing_address_line2 || "",
      billing_postal_code: data?.billing_postal_code || "",
      billing_city: data?.billing_city || "",
      billing_country: data?.billing_country || data?.country || "",
      shipping_same_as_billing: data?.shipping_same_as_billing ?? true,
      shipping_contact_name: data?.shipping_contact_name || "",
      shipping_service: data?.shipping_service || "",
      shipping_address_line1: data?.shipping_address_line1 || "",
      shipping_address_line2: data?.shipping_address_line2 || "",
      shipping_postal_code: data?.shipping_postal_code || "",
      shipping_city: data?.shipping_city || "",
      shipping_country: data?.shipping_country || data?.country || "",
    };
  }

  const fetchProfile = async () => {
    setErr("");
    setMsg("");
    setLoading(true);
    try {
      const res = await http.get("/user/profile/");
      setUser(res.data);
      setForm(toForm(res.data));
    } catch (e) {
      setErr(normalizeError(e, "Impossible de charger le profil. Vérifie ta connexion/auth."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  function updateField(key, value) {
    setForm((prev) => ({ ...(prev || {}), [key]: value }));
  }

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");
    setBusy(true);
    try {
      const res = await http.patch("/user/profile/", form, {
        headers: { "Content-Type": "application/json" },
      });
      setUser(res.data);
      setForm(toForm(res.data));
      setMsg("Profil mis à jour.");
    } catch (e2) {
      setErr(normalizeError(e2, "Erreur lors de la mise à jour du profil."));
    } finally {
      setBusy(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");

    if (!oldPassword || !newPassword) {
      setErr("Veuillez remplir ancien + nouveau mot de passe.");
      return;
    }
    if (newPassword.length < 8) {
      setErr("Le nouveau mot de passe doit contenir au moins 8 caractères.");
      return;
    }

    setBusy(true);
    try {
      await http.post("/user/change_password/", {
        old_password: oldPassword,
        new_password: newPassword,
      });
      setMsg("Mot de passe mis à jour.");
      setOldPassword("");
      setNewPassword("");
    } catch (e2) {
      setErr(normalizeError(e2, "Erreur lors du changement de mot de passe."));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteAccount = async () => {
    const ok = window.confirm("Supprimer votre compte ? Cette action est définitive.");
    if (!ok) return;

    setErr("");
    setMsg("");
    setBusy(true);
    try {
      await http.post("/user/delete_account/");
      clearAuthSession();
      window.location.href = "/login";
    } catch (e3) {
      setErr(normalizeError(e3, "Erreur lors de la suppression du compte."));
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = () => {
    clearAuthSession();
    window.location.href = "/login";
  };

  if (loading) return <div className="profile-wrap">Chargement...</div>;

  const status = (user?.rank || "normal").toLowerCase() === "master" ? "master" : "normal";

  return (
    <div className="profile-wrap">
      <div className="profile-topbar">
        <h2 className="profile-title">Mon profil</h2>
        <button className="profile-btn" onClick={handleLogout}>
          Déconnexion
        </button>
      </div>

      {err && <div className="profile-alert error">{err}</div>}
      {msg && <div className="profile-alert success">{msg}</div>}

      <div className="profile-card">
        <div className="profile-row">
          <div className="profile-label">Email</div>
          <div className="profile-value">{user?.email || "-"}</div>
        </div>
        <div className="profile-row">
          {/* <div className="profile-label">Statut</div>
          <div className="profile-value">
            <span className={`profile-badge ${status}`}>{status === "master" ? "Master" : "Normal"}</span>
          </div> */}
        </div>
      </div>

      <h3 className="profile-section-title"> société et adresses</h3>
      <form className="profile-form" onSubmit={handleSaveProfile}>
        <input className="profile-input" placeholder="Prénom" value={form?.first_name || ""} onChange={(e) => updateField("first_name", e.target.value)} />
        <input className="profile-input" placeholder="Nom" value={form?.last_name || ""} onChange={(e) => updateField("last_name", e.target.value)} />
        <input className="profile-input" placeholder="Société" value={form?.company_name || ""} onChange={(e) => updateField("company_name", e.target.value)} />
        <input className="profile-input" placeholder="SIRET / identifiant" value={form?.company_identifier || ""} onChange={(e) => updateField("company_identifier", e.target.value)} />
        <input className="profile-input" placeholder="Téléphone" value={form?.phone_number || ""} onChange={(e) => updateField("phone_number", e.target.value)} />
        <input className="profile-input" placeholder="Pays" value={form?.country || ""} onChange={(e) => updateField("country", e.target.value)} />
        <input className="profile-input" placeholder="Adresse facturation 1" value={form?.billing_address_line1 || ""} onChange={(e) => updateField("billing_address_line1", e.target.value)} />
        <input className="profile-input" placeholder="Adresse facturation 2" value={form?.billing_address_line2 || ""} onChange={(e) => updateField("billing_address_line2", e.target.value)} />
        <input className="profile-input" placeholder="Code postal facturation" value={form?.billing_postal_code || ""} onChange={(e) => updateField("billing_postal_code", e.target.value)} />
        <input className="profile-input" placeholder="Ville facturation" value={form?.billing_city || ""} onChange={(e) => updateField("billing_city", e.target.value)} />
        <input className="profile-input" placeholder="Pays facturation" value={form?.billing_country || ""} onChange={(e) => updateField("billing_country", e.target.value)} />
        <label className="auth-check">
          <input type="checkbox" checked={!!form?.shipping_same_as_billing} onChange={(e) => updateField("shipping_same_as_billing", e.target.checked)} />
          <span>Utiliser la même adresse pour la livraison</span>
        </label>
        {/* <input className="profile-input" placeholder="Nom livraison" value={form?.shipping_contact_name || ""} onChange={(e) => updateField("shipping_contact_name", e.target.value)} disabled={form?.shipping_same_as_billing} /> */}
        {/* <input className="profile-input" placeholder="Service livraison" value={form?.shipping_service || ""} onChange={(e) => updateField("shipping_service", e.target.value)} disabled={form?.shipping_same_as_billing} /> */}
        <input className="profile-input" placeholder="Adresse livraison 1" value={form?.shipping_address_line1 || ""} onChange={(e) => updateField("shipping_address_line1", e.target.value)} disabled={form?.shipping_same_as_billing} />
        <input className="profile-input" placeholder="Adresse livraison 2" value={form?.shipping_address_line2 || ""} onChange={(e) => updateField("shipping_address_line2", e.target.value)} disabled={form?.shipping_same_as_billing} />
        <input className="profile-input" placeholder="Code postal livraison" value={form?.shipping_postal_code || ""} onChange={(e) => updateField("shipping_postal_code", e.target.value)} disabled={form?.shipping_same_as_billing} />
        <input className="profile-input" placeholder="Ville livraison" value={form?.shipping_city || ""} onChange={(e) => updateField("shipping_city", e.target.value)} disabled={form?.shipping_same_as_billing} />
        <input className="profile-input" placeholder="Pays livraison" value={form?.shipping_country || ""} onChange={(e) => updateField("shipping_country", e.target.value)} disabled={form?.shipping_same_as_billing} />
        <button className="profile-primary" type="submit" disabled={busy}>{busy ? "..." : "Sauvegarder mon profil"}</button>
      </form>

      <h3 className="profile-section-title">Changer mot de passe</h3>
      <form className="profile-form" onSubmit={handleChangePassword}>
        <input className="profile-input" type="password" placeholder="Ancien mot de passe" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} required />
        <input className="profile-input" type="password" placeholder="Nouveau mot de passe" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
        <button className="profile-primary" type="submit" disabled={busy}>{busy ? "..." : "Mettre à jour"}</button>
      </form>

      <h3 className="profile-section-title">Supprimer le compte</h3>
      <button className="profile-danger" onClick={handleDeleteAccount} disabled={busy}>Supprimer mon compte</button>
    </div>
  );
}
